import { useCompanyGroupsApi } from "hooks/useCompanyGroupsApi";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";
import { companyGroupColorClass } from "utils/company-group-colors";

export type RhfCompanyGroupMultiSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control?: Control<T>;
  label?: string;
};

/**
 * 企業グループを複数選択するチェックボックス UI。
 *
 * react-hook-form の field 値は ``string[]`` (group ID のリスト) として扱う。
 * ISSUE-803 Phase 1C: 検索フィルタの「含む」「除く」セクションで使用する。
 * @param {RhfCompanyGroupMultiSelectProps<T>} props props
 * @return {JSX.Element} 複数選択 UI
 */
export function RhfCompanyGroupMultiSelect<T extends FieldValues>(
  props: RhfCompanyGroupMultiSelectProps<T>,
): JSX.Element {
  const { name, control, label } = props;
  const { field } = useController<T>({ name, control });
  const { data, loading } = useCompanyGroupsApi();

  const selected = (field.value as string[] | null | undefined) ?? [];

  const toggle = (id: string) => {
    const next = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    field.onChange(next);
  };

  if (loading) {
    return (
      <div className="text-xs text-base-content/60">
        企業グループを読み込み中...
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="text-xs text-base-content/60">
        企業グループが未登録です
      </div>
    );
  }

  return (
    <div>
      {label && (
        <div className="text-xs text-base-content/60 mb-1">{label}</div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {data.items.map(g => {
          const isSelected = selected.includes(g.id);
          return (
            <button
              type="button"
              key={g.id}
              onClick={() => toggle(g.id)}
              className={`px-2 py-1 rounded text-xs ${companyGroupColorClass(g.color)} ${
                isSelected
                  ? "ring-2 ring-offset-1 ring-slate-700"
                  : "opacity-60 hover:opacity-100"
              }`}
              aria-pressed={isSelected}
            >
              {isSelected ? "✓ " : ""}
              {g.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
